import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  OnDestroy,
  effect,
  input,
  output,
  signal,
  viewChild,
} from '@angular/core';
import * as L from 'leaflet';
import { FFSLC_REGIONS, FfslcRegionDefinition, getFfslcRegionForDepartment } from '../../data/ffslc-regions';
import { TranslatePipe } from '../../pipes/translate.pipe';

export type EventMapState = 'upcoming' | 'pending' | 'past';

export interface EventMapPoint {
  id: string;
  name: string;
  city?: string;
  departmentCode?: string;
  latitude: number;
  longitude: number;
  precision?: 'venue' | 'address' | 'postal_code' | 'city_center';
  state: EventMapState;
}

@Component({
  selector: 'app-event-map',
  standalone: true,
  imports: [TranslatePipe],
  template: `
    <div class="overflow-hidden rounded-[1.75rem] bg-slate-100 dark:bg-slate-800">
      <div class="relative">
        <div #mapContainer [class.is-thematic-map]="showTerritories()"
          class="h-[390px] w-full lg:h-[540px] 2xl:h-[620px]" role="region" [attr.aria-label]="'accessibility.map' | translate"></div>
        <button type="button" (click)="recenter()"
          class="accent-focus absolute left-3 top-3 z-[500] flex items-center gap-2 rounded-xl border border-transparent bg-white/95 px-3 py-2 text-[10px] font-black uppercase tracking-wider text-slate-700 shadow-lg backdrop-blur transition hover:bg-white dark:bg-slate-900/95 dark:text-slate-200 dark:hover:bg-slate-900"
          [attr.aria-label]="'accessibility.recenterMap' | translate">
          <svg class="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" aria-hidden="true">
            <circle cx="12" cy="12" r="7" stroke-width="2" />
            <path d="M12 2v3M12 19v3M2 12h3M19 12h3" stroke-width="2" stroke-linecap="round" />
          </svg>
          {{ 'accessibility.recenterMap' | translate }}
        </button>
      </div>
      <div class="border-t border-slate-200 bg-white/95 p-3 text-[10px] text-slate-600 dark:border-slate-700 dark:bg-slate-900/95 dark:text-slate-300 sm:p-4"
        role="region" [attr.aria-label]="'races.mapLegend.title' | translate">
        <p class="mb-2 font-black uppercase tracking-[0.16em] text-slate-900 dark:text-slate-100">
          {{ 'races.mapLegend.title' | translate }}
        </p>
        @if (showTerritories()) {
        <div class="grid grid-cols-2 gap-x-4 gap-y-1.5 lg:grid-cols-3">
          @for (federation of legendRegions; track federation.code) {
          <div class="flex min-w-0 items-start gap-1.5 leading-tight">
            <span class="h-3 w-3 shrink-0 rounded-sm border border-white/80 shadow-sm dark:border-slate-700"
              [style.background-color]="federation.color" aria-hidden="true"></span>
            <span class="break-words"><strong>{{ federation.code }}</strong> · {{ federation.shortName }}</span>
          </div>
          }
        </div>
        <p class="mt-2 border-t border-slate-200 pt-2 font-bold text-slate-500 dark:border-slate-700 dark:text-slate-400">
          {{ 'races.mapLegend.territoryHint' | translate }}
        </p>
        }
        @if (detailMode()) {
        <div class="flex flex-wrap gap-x-3 gap-y-1.5" [class.mt-2]="showTerritories()">
          <span><span class="mr-1 text-sky-500">●</span>{{ 'races.mapLegend.upcoming' | translate }}</span>
          <span><span class="mr-1 text-amber-500">●</span>{{ 'races.mapLegend.pending' | translate }}</span>
          <span><span class="mr-1 text-emerald-600">●</span>{{ 'races.mapLegend.past' | translate }}</span>
        </div>
        <p class="mt-1 font-bold text-slate-500 dark:text-slate-400">
          {{ 'races.mapLegend.precisionHint' | translate }}
        </p>
        }
      </div>
    </div>
  `,
  styles: [`
    :host { display: block; }
    :host ::ng-deep .leaflet-container {
      font-family: inherit;
      background: #e2e8f0;
    }
    :host ::ng-deep .leaflet-container.is-thematic-map {
      background-color: #f8fafc;
      background-image: radial-gradient(circle at 1px 1px, rgba(148, 163, 184, .28) 1px, transparent 0);
      background-size: 18px 18px;
    }
    :host ::ng-deep .leaflet-control-zoom {
      overflow: hidden;
      border: 0;
      border-radius: 14px;
      box-shadow: 0 8px 24px rgba(15, 23, 42, .22);
    }
    :host ::ng-deep .leaflet-control-zoom a {
      width: 38px;
      height: 38px;
      line-height: 36px;
      border-color: #e2e8f0;
      color: #0f172a;
      font-size: 22px;
      font-weight: 800;
    }
    :host-context(.dark) ::ng-deep .leaflet-container.is-thematic-map {
      background-color: #0f172a;
      background-image: radial-gradient(circle at 1px 1px, rgba(148, 163, 184, .14) 1px, transparent 0);
    }
    :host-context(.dark) ::ng-deep .leaflet-control-zoom-in,
    :host-context(.dark) ::ng-deep .leaflet-control-zoom-out {
      background: #1e293b;
      border-color: #334155;
      color: #e2e8f0;
    }
    :host ::ng-deep .event-cluster-selected > div {
      outline: 5px solid rgba(99, 102, 241, .45);
      outline-offset: 2px;
    }
    :host ::ng-deep .territory-federation-hovered {
      filter: brightness(1.12) saturate(1.08);
    }
    :host ::ng-deep .federation-label-marker {
      background: transparent;
      border: 0;
      pointer-events: none;
    }
    :host ::ng-deep .federation-label {
      display: flex;
      min-width: 43px;
      align-items: center;
      justify-content: center;
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      border: 2px solid #0f172a;
      border-radius: 9px;
      background: rgba(255, 255, 255, .96);
      box-shadow: 0 4px 12px rgba(15, 23, 42, .2);
      color: #0f172a;
      font: 900 14px / 1 inherit;
      letter-spacing: -.04em;
      padding: 6px 7px;
      white-space: nowrap;
    }
    :host-context(.dark) ::ng-deep .federation-label {
      border-color: #e2e8f0;
      background: rgba(15, 23, 42, .96);
      color: #f8fafc;
    }
  `],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EventMapComponent implements AfterViewInit, OnDestroy {
  private static readonly detailZoom = 7.25;

  points = input<EventMapPoint[]>([]);
  selectedEventId = input<string | null>(null);
  userPosition = input<{ latitude: number; longitude: number } | null>(null);
  showTerritories = input(false);
  activeRegion = input<string | null>(null);
  activeFederationRegion = input<string | null>(null);
  activeDepartment = input<string | null>(null);
  eventSelected = output<string>();
  regionSelected = output<string>();
  federationRegionSelected = output<string>();
  departmentSelected = output<string>();
  readonly legendRegions = FFSLC_REGIONS;

  private readonly mapContainer = viewChild.required<ElementRef<HTMLDivElement>>('mapContainer');
  private map?: L.Map;
  private readonly eventsLayer = L.layerGroup();
  private readonly userLayer = L.layerGroup();
  private departmentsLayer?: L.GeoJSON;
  private readonly federationRegionLayers = new Map<string, L.GeoJSON>();
  private federationLabelsLayer?: L.LayerGroup;
  private departmentsData?: any;
  private regionsData?: any;
  private territoriesPromise?: Promise<void>;
  private readonly departmentBounds = new Map<string, L.LatLngBounds>();
  private readonly federationBounds = new Map<string, L.LatLngBounds>();
  private readonly regionBounds = new Map<string, L.LatLngBounds>();
  private readonly regionNameByCode = new Map<string, string>();
  private expandedFederationCode: string | null = null;
  readonly detailMode = signal(true);
  private readonly markerByEventId = new Map<string, L.CircleMarker>();
  private readonly clusterByEventId = new Map<string, L.Marker>();
  private pointSignature = '';
  private resizeObserver?: ResizeObserver;
  private resizeFrame?: number;
  private delayedRefit?: number;
  private territoryFilterSignature = '';
  private readonly federationHoverTimers = new Map<string, number>();

  constructor() {
    effect(() => {
      const points = this.points();
      const signature = points
        .map(point => `${point.id}:${point.latitude}:${point.longitude}:${point.state}:${point.precision}`)
        .join('|');
      if (!this.map || signature === this.pointSignature) return;
      this.pointSignature = signature;
      this.renderPoints(points);
    });

    effect(() => {
      const selectedId = this.selectedEventId();
      if (this.map) this.highlightSelected(selectedId);
    });

    effect(() => {
      const position = this.userPosition();
      if (this.map) this.renderUserPosition(position);
    });

    effect(() => {
      const showTerritories = this.showTerritories();
      const region = this.activeRegion();
      const federationRegion = this.activeFederationRegion();
      const department = this.activeDepartment();
      this.points();
      const territoryFilterChanged = this.syncExpandedFederation(region, federationRegion, department);
      if (!this.map) return;
      if (!showTerritories) {
        this.removeTerritories();
        this.detailMode.set(true);
        return;
      }
      void this.ensureTerritories().then(() => {
        this.renderTerritories();
        this.fitTerritoryBounds(region, federationRegion, department, territoryFilterChanged);
      });
    });
  }

  ngAfterViewInit(): void {
    this.map = L.map(this.mapContainer().nativeElement, {
      attributionControl: !this.showTerritories(),
      zoomControl: false,
      scrollWheelZoom: false,
      zoomSnap: .25,
      zoomDelta: .5,
    }).setView([50.35, 2.8], 7);
    L.control.zoom({ position: 'topright' }).addTo(this.map);
    if (!this.showTerritories()) {
      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        maxZoom: 18,
        attribution: '&copy; OpenStreetMap contributors · Limites administratives Etalab',
      }).addTo(this.map);
    }
    this.map.on('zoomend', this.handleZoomChange);
    this.eventsLayer.addTo(this.map);
    this.userLayer.addTo(this.map);
    this.pointSignature = '';
    this.detailMode.set(this.isDetailMode());
    this.renderPoints(this.points(), !this.showTerritories());
    this.renderUserPosition(this.userPosition(), !this.showTerritories());
    if (this.showTerritories()) {
      void this.ensureTerritories().then(() => {
        this.renderTerritories();
        this.fitTerritoryBounds(this.activeRegion(), this.activeFederationRegion(), this.activeDepartment());
      });
    }
    this.resizeObserver = new ResizeObserver(() => this.scheduleResizeAndFit());
    this.resizeObserver.observe(this.mapContainer().nativeElement);
    this.scheduleResizeAndFit();
    this.delayedRefit = window.setTimeout(() => this.scheduleResizeAndFit(), 750);
  }

  ngOnDestroy(): void {
    this.resizeObserver?.disconnect();
    if (this.resizeFrame !== undefined) window.cancelAnimationFrame(this.resizeFrame);
    if (this.delayedRefit !== undefined) window.clearTimeout(this.delayedRefit);
    this.federationHoverTimers.forEach(timer => window.clearTimeout(timer));
    this.federationHoverTimers.clear();
    this.map?.remove();
  }

  recenter(): void {
    this.map?.invalidateSize({ pan: false });
    this.fitVisibleBounds();
  }

  private scheduleResizeAndFit(): void {
    if (this.resizeFrame !== undefined) window.cancelAnimationFrame(this.resizeFrame);
    this.resizeFrame = window.requestAnimationFrame(() => {
      this.resizeFrame = undefined;
      this.map?.invalidateSize({ pan: false });
      this.fitVisibleBounds();
    });
  }

  private ensureTerritories(): Promise<void> {
    if (this.territoriesPromise) return this.territoriesPromise;
    this.territoriesPromise = Promise.all([
      fetch('/data/departements-1000m.geojson').then(response => {
        if (!response.ok) throw new Error(`Department boundaries unavailable (${response.status})`);
        return response.json();
      }),
      fetch('/data/regions-1000m.geojson').then(response => {
        if (!response.ok) throw new Error(`Region boundaries unavailable (${response.status})`);
        return response.json();
      }),
    ]).then(([departments, regions]) => {
      const metropolitanRegionCodes = new Set([
        '11', '24', '27', '28', '32', '44', '52', '53', '75', '76', '84', '93', '94',
      ]);
      this.departmentsData = {
        ...departments,
        features: departments.features.filter((feature: any) =>
          metropolitanRegionCodes.has(String(feature.properties?.region))),
      };
      this.regionsData = {
        ...regions,
        features: regions.features.filter((feature: any) =>
          metropolitanRegionCodes.has(String(feature.properties?.code))),
      };
      this.regionNameByCode.clear();
      this.regionBounds.clear();
      this.regionsData.features.forEach((feature: any) => {
        const name = String(feature.properties.nom);
        this.regionNameByCode.set(String(feature.properties.code), name);
        const bounds = L.geoJSON(feature).getBounds();
        if (name && bounds.isValid()) this.regionBounds.set(name, bounds);
      });
    }).catch(error => {
      this.territoriesPromise = undefined;
      console.error('Unable to load administrative boundaries', error);
    });
    return this.territoriesPromise;
  }

  private renderTerritories(): void {
    if (!this.map || !this.departmentsData || !this.regionsData) return;
    this.removeTerritories();
    this.departmentBounds.clear();
    this.federationBounds.clear();

    // Keep bounds indexed even while the department layer is hidden. They are
    // needed to fit the map after a region or department is selected.
    this.departmentsData.features.forEach((feature: any) => {
      const code = String(feature.properties?.['code'] ?? '');
      const layer = L.geoJSON(feature);
      if (code && layer.getBounds().isValid()) this.departmentBounds.set(code, layer.getBounds());
    });

    const expandedFederationCode = this.getExpandedFederationCode();
    const detailMode = this.isDetailMode();
    this.detailMode.set(detailMode);
    if (detailMode) {
      const visibleDepartments = {
        ...this.departmentsData,
        features: this.getVisibleDepartmentFeatures(expandedFederationCode),
      };

      this.departmentsLayer = L.geoJSON(visibleDepartments, {
        style: feature => this.departmentStyle(feature),
        onEachFeature: (feature, layer) => {
          const code = String(feature.properties?.['code'] ?? '');
          const name = String(feature.properties?.['nom'] ?? code);
          const regionCode = String(feature.properties?.['region'] ?? '');
          const regionName = this.regionNameByCode.get(regionCode);
          const federation = getFfslcRegionForDepartment(code);
          const count = this.points().filter(point => point.departmentCode === code).length;
          layer.bindTooltip(`${name} (${code})${count ? ` · ${count} événement${count > 1 ? 's' : ''}` : ''}`, {
            sticky: true,
          });
          layer.on('add', () => {
            const element = (layer as L.Path).getElement();
            if (!element) return;
            element.setAttribute('role', 'button');
            element.setAttribute('tabindex', '0');
            element.setAttribute('aria-label', `${name} (${code}), ${federation?.name ?? 'région inconnue'}`);
            element.addEventListener('click', () => {
              if (federation) this.selectTerritory(federation, regionName, code);
            });
            element.addEventListener('keydown', event => {
              const keyboardEvent = event as KeyboardEvent;
              if (keyboardEvent.key !== 'Enter' && keyboardEvent.key !== ' ') return;
              event.preventDefault();
              if (federation) this.selectTerritory(federation, regionName, code);
            });
          });
          layer.on({
            mouseover: () => (layer as L.Path).setStyle({ weight: 2, fillOpacity: .48 }),
            mouseout: () => this.departmentsLayer?.resetStyle(layer as L.Path),
          });
        },
      }).addTo(this.map);
    }

    // Overview: draw each FFSLC region as one colored group. Internal
    // department borders use the same color and are only exposed after click.
    for (const federation of FFSLC_REGIONS) {
      // Once a federation is selected, retain its silhouette as context while
      // exposing the department boundaries above it.
      if (expandedFederationCode && federation.code !== expandedFederationCode) continue;
      const features = this.departmentsData.features.filter((feature: any) =>
        federation.departments.includes(String(feature.properties?.['code'] ?? '').toUpperCase()),
      );
      if (!features.length) continue;
      const bounds = new L.LatLngBounds([]);
      features.forEach((feature: any) => {
        const departmentBounds = this.departmentBounds.get(String(feature.properties?.['code'] ?? ''));
        if (departmentBounds) bounds.extend(departmentBounds);
      });
      if (bounds.isValid()) this.federationBounds.set(federation.code, bounds);

      const eventCount = this.points().filter(point =>
        getFfslcRegionForDepartment(point.departmentCode)?.code === federation.code,
      ).length;
      let overviewLayer!: L.GeoJSON;
      overviewLayer = L.geoJSON({ type: 'FeatureCollection', features } as any, {
        // Department paths own the interaction in the detailed view.
        interactive: !detailMode,
        style: () => this.federationOverviewStyle(federation, expandedFederationCode, detailMode),
        onEachFeature: (_feature, layer) => {
          layer.bindTooltip(
            `${federation.name}${eventCount ? ` · ${eventCount} événement${eventCount > 1 ? 's' : ''}` : ''} · cliquez pour explorer`,
            { sticky: true },
          );
          layer.on({
            // A federation is made of several department geometries. Hover
            // the whole federation so the interaction matches the visual
            // shape shown in the national overview.
            mouseover: () => this.setFederationHover(federation, overviewLayer, true),
            mouseout: () => this.setFederationHover(federation, overviewLayer, false),
            click: () => this.selectFederation(federation.code),
          });
        },
      }).addTo(this.map);
      this.federationRegionLayers.set(federation.code, overviewLayer);
    }
    this.departmentsLayer?.bringToFront();
    if (!detailMode) this.renderFederationLabels();
    this.eventsLayer.eachLayer((layer: L.Layer) => (layer as L.Path).bringToFront?.());
    this.renderPoints(this.points(), false);
    this.renderUserPosition(this.userPosition(), false);
  }

  private getExpandedFederationCode(): string | null {
    if (this.activeDepartment()) {
      const federation = getFfslcRegionForDepartment(this.activeDepartment());
      if (federation) return federation.code;
    }
    return this.activeFederationRegion() ?? this.expandedFederationCode;
  }

  private selectFederation(code: string): void {
    this.expandedFederationCode = code;
    this.renderTerritories();
    this.federationRegionSelected.emit(code);
    this.fitTerritoryBounds(null, code, null);
  }

  private federationOverviewStyle(
    federation: FfslcRegionDefinition,
    expandedFederationCode: string | null,
    detailMode: boolean,
  ): L.PathOptions {
    const active = federation.code === expandedFederationCode;
    return {
      className: `territory-federation territory-federation-${federation.code}`,
      color: federation.color,
      // The same color on both fill and stroke visually dissolves the
      // departments into one clean federation shape at the overview level.
      weight: detailMode ? 0 : active ? 2 : 1.2,
      opacity: detailMode ? 0 : 1,
      fillColor: federation.color,
      // An opaque fill masks the individual department strokes so the
      // overview reads as nine simple federation areas, not 96 departments.
      fillOpacity: detailMode ? 0 : 1,
    };
  }

  private removeTerritories(): void {
    if (!this.map) return;
    this.federationHoverTimers.forEach(timer => window.clearTimeout(timer));
    this.federationHoverTimers.clear();
    if (this.departmentsLayer) this.map.removeLayer(this.departmentsLayer);
    this.federationRegionLayers.forEach(layer => this.map?.removeLayer(layer));
    this.federationRegionLayers.clear();
    if (this.federationLabelsLayer) this.map.removeLayer(this.federationLabelsLayer);
    this.departmentsLayer = undefined;
    this.federationLabelsLayer = undefined;
  }

  private setFederationHover(
    federation: FfslcRegionDefinition,
    layer: L.GeoJSON,
    hovered: boolean,
  ): void {
    const existingTimer = this.federationHoverTimers.get(federation.code);
    if (existingTimer !== undefined) {
      window.clearTimeout(existingTimer);
      this.federationHoverTimers.delete(federation.code);
    }

    if (hovered) {
      layer.eachLayer((child: L.Layer) => {
        const path = child as L.Path;
        path.setStyle({ weight: 2.5, fillOpacity: 1, opacity: 1 });
        path.getElement()?.classList.add('territory-federation-hovered');
      });
      return;
    }

    // Moving between adjacent departments fires mouseout then mouseover.
    // Delay the reset by one frame so the federation stays highlighted while
    // the pointer crosses an internal department boundary.
    const timer = window.setTimeout(() => {
      this.federationHoverTimers.delete(federation.code);
      layer.eachLayer((child: L.Layer) => {
        const path = child as L.Path;
        layer.resetStyle(path);
        path.getElement()?.classList.remove('territory-federation-hovered');
      });
    }, 20);
    this.federationHoverTimers.set(federation.code, timer);
  }

  private departmentStyle(feature?: any): L.PathOptions {
    const code = String(feature?.properties?.code ?? '');
    const federation = getFfslcRegionForDepartment(code);
    const activeDepartment = code === this.activeDepartment();
    const activeFederation = federation?.code === this.activeFederationRegion();
    const hasEvents = this.points().some(point => point.departmentCode === code);
    return {
      className: `territory-department territory-department-${code.toLowerCase()}`,
      color: activeDepartment ? '#312e81' : '#ffffff',
      weight: activeDepartment ? 3 : activeFederation ? 1.5 : 1.1,
      opacity: .96,
      fillColor: federation?.color ?? '#94a3b8',
      // Keep the selected region light enough for department boundaries and
      // labels to remain readable.
      fillOpacity: activeDepartment ? .82 : activeFederation ? .74 : hasEvents ? .68 : .45,
    };
  }

  private selectTerritory(federation: FfslcRegionDefinition, regionName: string | undefined, departmentCode: string): void {
    if (federation.code !== this.activeFederationRegion()) {
      this.federationRegionSelected.emit(federation.code);
    } else {
      this.departmentSelected.emit(departmentCode);
    }
  }

  private renderFederationLabels(): void {
    if (!this.map) return;
    this.federationLabelsLayer = L.layerGroup().addTo(this.map);
    for (const federation of FFSLC_REGIONS) {
      const bounds = this.departmentBoundsForFederation(federation);
      if (!bounds?.isValid()) continue;
      L.marker(bounds.getCenter(), {
        interactive: false,
        icon: L.divIcon({
          className: 'federation-label-marker',
          html: `<div class="federation-label" aria-hidden="true">${federation.code}</div>`,
          iconSize: [60, 34],
          iconAnchor: [30, 17],
        }),
      }).addTo(this.federationLabelsLayer);
    }
  }

  private departmentBoundsForFederation(federation: FfslcRegionDefinition): L.LatLngBounds | null {
    const bounds = new L.LatLngBounds([]);
    for (const code of federation.departments) {
      const departmentBounds = this.departmentBounds.get(code);
      if (departmentBounds) bounds.extend(departmentBounds);
    }
    return bounds.isValid() ? bounds : null;
  }

  private fitTerritoryBounds(
    region: string | null,
    federationCode: string | null,
    department: string | null,
    animate = false,
  ): void {
    if (!this.map || !this.showTerritories()) return;
    const departmentBounds = department ? this.departmentBounds.get(department) : undefined;
    if (departmentBounds?.isValid()) {
      this.moveToBounds(departmentBounds, [30, 30], 10, animate);
      return;
    }
    const federation = federationCode ? FFSLC_REGIONS.find(item => item.code === federationCode) : undefined;
    const federationBounds = federation ? this.departmentBoundsForFederation(federation) : undefined;
    if (federationBounds?.isValid()) {
      this.moveToBounds(federationBounds, [28, 28], 8, animate);
      return;
    }
    const regionBounds = region
      ? [...this.regionBounds.entries()].find(([name]) => this.normalize(name) === this.normalize(region))?.[1]
      : undefined;
    if (regionBounds?.isValid()) {
      this.moveToBounds(regionBounds, [28, 28], 8, animate);
      return;
    }
    const franceBounds = L.latLngBounds([...this.federationBounds.values()].flatMap(bounds => [
      bounds.getSouthWest(),
      bounds.getNorthEast(),
    ]));
    if (franceBounds.isValid()) this.moveToBounds(franceBounds, [24, 24], 6, animate);
  }

  private moveToBounds(
    bounds: L.LatLngBounds,
    padding: [number, number],
    maxZoom: number,
    animate: boolean,
  ): void {
    if (!this.map) return;
    const options: L.FitBoundsOptions = { padding, maxZoom };
    if (animate) {
      this.map.flyToBounds(bounds, {
        ...options,
        duration: .8,
        easeLinearity: .25,
      });
      return;
    }
    this.map.fitBounds(bounds, options);
  }

  private syncExpandedFederation(
    region: string | null,
    federation: string | null,
    department: string | null,
  ): boolean {
    const signature = `${region ?? ''}|${federation ?? ''}|${department ?? ''}`;
    if (signature === this.territoryFilterSignature) return false;
    this.territoryFilterSignature = signature;
    this.expandedFederationCode = federation ?? getFfslcRegionForDepartment(department)?.code ?? null;
    return true;
  }

  private getVisibleDepartmentFeatures(expandedFederationCode: string | null): any[] {
    if (!this.departmentsData) return [];
    if (expandedFederationCode) {
      return this.departmentsData.features.filter((feature: any) =>
        getFfslcRegionForDepartment(String(feature.properties?.['code'] ?? ''))?.code === expandedFederationCode,
      );
    }
    const activeRegion = this.activeRegion();
    if (!activeRegion) return this.departmentsData.features;
    return this.departmentsData.features.filter((feature: any) =>
      this.normalize(this.regionNameByCode.get(String(feature.properties?.['region'] ?? '')) ?? '') ===
      this.normalize(activeRegion),
    );
  }

  private isDetailMode(): boolean {
    if (!this.showTerritories()) return true;
    return !!this.getExpandedFederationCode() || !!this.activeRegion() ||
      (this.map?.getZoom() ?? 0) >= EventMapComponent.detailZoom;
  }

  private handleZoomChange = (): void => {
    if (!this.showTerritories()) return;
    const isDetailMode = this.isDetailMode();
    if (isDetailMode === this.detailMode()) return;
    this.detailMode.set(isDetailMode);
    this.renderTerritories();
  };

  private normalize(value: string): string {
    return value.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
  }

  private renderPoints(points: EventMapPoint[], fit = true): void {
    if (!this.map) return;
    this.eventsLayer.clearLayers();
    this.markerByEventId.clear();
    this.clusterByEventId.clear();

    // The national view remains an uncluttered territorial entry point. Event
    // information appears as soon as a user selects a federation or zooms in.
    if (this.showTerritories() && !this.isDetailMode()) return;

    const groups = new Map<string, EventMapPoint[]>();
    points.forEach(point => {
      const key = `${point.latitude.toFixed(3)}:${point.longitude.toFixed(3)}`;
      const group = groups.get(key) ?? [];
      group.push(point);
      groups.set(key, group);
    });

    for (const group of groups.values()) {
      if (group.length > 1) this.addCluster(group);
      else this.addPoint(group[0]);
    }

    if (fit) this.fitVisibleBounds();
    this.highlightSelected(this.selectedEventId());
  }

  private addPoint(point: EventMapPoint): void {
    const marker = L.circleMarker([point.latitude, point.longitude], {
      radius: 8,
      color: '#ffffff',
      weight: 3,
      fillColor: this.stateColor(point.state),
      fillOpacity: point.precision === 'venue' || point.precision === 'address' ? 1 : 0.72,
      dashArray: point.precision === 'venue' || point.precision === 'address' ? undefined : '4 3',
    });
    const tooltip = document.createElement('div');
    const title = document.createElement('strong');
    title.textContent = point.name;
    tooltip.append(title);
    if (point.city) {
      const location = document.createElement('div');
      location.textContent = `${point.city}${point.departmentCode ? ` (${point.departmentCode})` : ''}`;
      tooltip.append(location);
    }
    marker.bindTooltip(tooltip, { direction: 'top', offset: [0, -8] });
    marker.on('click', () => this.eventSelected.emit(point.id));
    marker.addTo(this.eventsLayer);
    this.markerByEventId.set(point.id, marker);
  }

  private addCluster(points: EventMapPoint[]): void {
    const [first] = points;
    const marker = L.marker([first.latitude, first.longitude], {
      icon: L.divIcon({
        className: '',
        html: `<div style="width:38px;height:38px;border-radius:9999px;background:#0f172a;color:white;border:3px solid white;display:flex;align-items:center;justify-content:center;font-weight:900;box-shadow:0 4px 14px rgba(15,23,42,.3)">${points.length}</div>`,
        iconSize: [38, 38],
        iconAnchor: [19, 19],
      }),
    });
    const popup = document.createElement('div');
    popup.className = 'min-w-52 space-y-1';
    points.forEach(point => {
      const button = document.createElement('button');
      button.type = 'button';
      button.textContent = point.name;
      button.style.cssText = 'display:block;width:100%;padding:7px;text-align:left;font-weight:700;border-bottom:1px solid #e2e8f0';
      button.addEventListener('click', () => {
        this.eventSelected.emit(point.id);
        this.map?.closePopup();
      });
      popup.append(button);
    });
    marker.bindPopup(popup).addTo(this.eventsLayer);
    points.forEach(point => this.clusterByEventId.set(point.id, marker));
  }

  private highlightSelected(selectedId: string | null): void {
    for (const [eventId, marker] of this.markerByEventId) {
      const selected = eventId === selectedId;
      marker.setRadius(selected ? 12 : 8);
      marker.setStyle({ weight: selected ? 5 : 3 });
      if (selected) marker.bringToFront();
    }
    const clusters = new Set(this.clusterByEventId.values());
    clusters.forEach(marker => marker.getElement()?.classList.remove('event-cluster-selected'));
    if (selectedId) {
      this.clusterByEventId.get(selectedId)?.getElement()?.classList.add('event-cluster-selected');
    }
  }

  private renderUserPosition(position: { latitude: number; longitude: number } | null, fit = true): void {
    this.userLayer.clearLayers();
    if (!position || (this.showTerritories() && !this.isDetailMode())) return;
    L.circleMarker([position.latitude, position.longitude], {
      radius: 7,
      color: '#ffffff',
      weight: 3,
      fillColor: '#4f46e5',
      fillOpacity: 1,
    }).bindTooltip('Votre position').addTo(this.userLayer);
    if (fit) this.fitVisibleBounds();
  }

  private fitVisibleBounds(): void {
    if (!this.map) return;
    if (this.showTerritories() && this.federationBounds.size > 0) {
      this.fitTerritoryBounds(this.activeRegion(), this.activeFederationRegion(), this.activeDepartment());
      return;
    }
    const coordinates: L.LatLngTuple[] = this.points().map(point => [
      point.latitude,
      point.longitude,
    ]);
    const user = this.userPosition();
    if (user) coordinates.push([user.latitude, user.longitude]);
    const bounds = L.latLngBounds(coordinates);
    if (!bounds.isValid()) return;
    if (coordinates.length === 1) this.map.setView(bounds.getCenter(), 11);
    else this.map.fitBounds(bounds, { padding: [36, 36], maxZoom: 11 });
  }

  private stateColor(state: EventMapState): string {
    if (state === 'past') return '#059669';
    if (state === 'pending') return '#f59e0b';
    return '#0ea5e9';
  }
}
